import type { Dispatch, SetStateAction } from "react";
import { Box, Button, Collapse, IconButton, InputAdornment, MenuItem, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { AppliedFilterChips, type AppliedFilterChip } from "../../components/AppliedFilterChips";
import { CategorySearchSelect } from "../../components/CategorySearchSelect";
import { DateRangePickerField } from "../../components/DateRangePickerField";
import { AccountSelect, UserSelect } from "../../components/FinanceSelects";
import { FilterSelect } from "../../components/FilterSelect";
import { TagFilterSelect } from "../../components/TagFilterSelect";
import { transactionTypeLabels } from "../../lib/labels";
import type { Account, Category, Member, Tag, TransactionType } from "../../lib/types";
import { emptyRecordFilters, type RecordFilters } from "./recordsFilters";

export function RecordsFilterPanel({
  filters,
  setFilters,
  filtersOpen,
  setFiltersOpen,
  search,
  setSearch,
  categories,
  filterCategories,
  accounts,
  members,
  tags,
  chips,
  onClearFilter,
}: {
  filters: RecordFilters;
  setFilters: Dispatch<SetStateAction<RecordFilters>>;
  filtersOpen: boolean;
  setFiltersOpen: Dispatch<SetStateAction<boolean>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  categories: Category[];
  filterCategories: Category[];
  accounts: Account[];
  members: Member[];
  tags: Tag[];
  chips: AppliedFilterChip<keyof RecordFilters>[];
  onClearFilter: (key: keyof RecordFilters) => void;
}) {
  const hasExpandedFilters = Boolean(
    filters.type || filters.categoryId || filters.uncategorized || filters.userId ||
    filters.accountId || filters.tagIds.length || filters.dateFrom || filters.dateTo,
  );
  return (
    <Box className="records-search-block">
      <Box className="records-search-row">
        <TextField
          className="records-search-input"
          value={search}
          placeholder="Search records"
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton className="records-search-clear" size="small" type="button" aria-label="Clear search" title="Clear search" onClick={() => setSearch("")} edge="end">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
        <Button
          className="records-filter-toggle"
          type="button"
          size="small"
          variant={filtersOpen ? "contained" : "outlined"}
          disableElevation
          startIcon={<FilterAltOutlinedIcon fontSize="small" />}
          aria-label={`Filters${chips.length ? ` (${chips.length} active)` : ""}`}
          aria-expanded={filtersOpen}
          title="Filter settings"
          onClick={() => setFiltersOpen((value) => !value)}
        >
          <span className="records-filter-toggle-label">Filters{chips.length ? ` (${chips.length})` : ""}</span>
        </Button>
      </Box>
      <Collapse in={filtersOpen}>
        <Box className="records-filter-panel">
          <FilterSelect
            label="Type"
            value={filters.type}
            displayEmpty
            renderValue={(value) => value === "linked" ? "Linked transfer" : value ? transactionTypeLabels[value as TransactionType] : "All types"}
            onChange={(value) => {
              const type = value as RecordFilters["type"];
              setFilters((current) => {
                const selectedCategory = categories.find((category) => category.id === current.categoryId);
                return {
                  ...current,
                  type,
                  categoryId: selectedCategory && type && selectedCategory.type !== type ? "" : current.categoryId,
                };
              });
            }}
          >
            <MenuItem value="">All types</MenuItem>
            <MenuItem value="expense">Expense</MenuItem>
            <MenuItem value="income">Income</MenuItem>
            <MenuItem value="transfer">Transfer</MenuItem>
            <MenuItem value="correction">Correction</MenuItem>
            <MenuItem value="linked">Linked transfer</MenuItem>
          </FilterSelect>
          <Box className="records-field">
            <CategorySearchSelect
              size="small"
              label="Category"
              value={filters.categoryId}
              emptyLabel="All categories"
              categories={filterCategories}
              allCategories={categories}
              disableMobileSearchAutoFocus
              onChange={(categoryId) => setFilters((current) => ({ ...current, categoryId, uncategorized: false }))}
            />
          </Box>
          <Box className="records-field">
            <UserSelect size="small" label="Person" members={members} emptyLabel="All people" filterDropdown value={filters.userId} onChange={(userId) => setFilters((current) => ({ ...current, userId }))} />
          </Box>
          <Box className="records-field">
            <AccountSelect size="small" label="Account" accounts={accounts} members={members} emptyLabel="All accounts" filterDropdown value={filters.accountId} onChange={(accountId) => setFilters((current) => ({ ...current, accountId }))} />
          </Box>
          <Box className="records-field">
            <TagFilterSelect tags={tags} value={filters.tagIds} onChange={(tagIds) => setFilters((current) => ({ ...current, tagIds }))} />
          </Box>
          <DateRangePickerField
            size="small"
            label="Date range"
            dateFrom={filters.dateFrom}
            dateTo={filters.dateTo}
            clearable
            onChange={(dateFrom, dateTo) => setFilters((current) => ({ ...current, dateFrom, dateTo }))}
          />
          <Button size="small" variant="outlined" type="button" disabled={!hasExpandedFilters} onClick={() => setFilters(emptyRecordFilters())}>
            Clear
          </Button>
        </Box>
      </Collapse>
      <AppliedFilterChips chips={chips} className="records-filter-chips" onDelete={onClearFilter} />
    </Box>
  );
}
